<template>
  <div class="space-y-6">
    <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">成果归属</h3>
    
    <!-- 成果类型选择 -->
    <div>
      <label class="block mb-3">
        成果类型 <span class="text-red-500">*</span>
      </label>
      <div class="flex gap-6">
        <label class="flex items-center gap-3 cursor-pointer group">
          <div class="relative">
            <input
              type="radio"
              value="individual"
              v-model="localAchievementType"
              @change="handleAchievementTypeChange"
              class="sr-only"
            />
            <div :class="[
              'w-5 h-5 rounded-full border-2 transition-all duration-200',
              localAchievementType === 'individual'
                ? 'border-blue-600 bg-blue-600'
                : 'border-gray-300 bg-white group-hover:border-blue-400'
            ]">
              <div v-if="localAchievementType === 'individual'" class="w-full h-full rounded-full bg-white scale-[0.4] transition-transform duration-200"></div>
            </div>
          </div>
          <span :class="[
            'transition-colors duration-200',
            localAchievementType === 'individual' 
              ? 'text-gray-900' 
              : 'text-gray-600 group-hover:text-gray-800'
          ]">
            单人成果
          </span>
        </label>
        <label class="flex items-center gap-3 cursor-pointer group">
          <div class="relative">
            <input
              type="radio"
              value="team"
              v-model="localAchievementType"
              @change="handleAchievementTypeChange"
              class="sr-only"
            />
            <div :class="[
              'w-5 h-5 rounded-full border-2 transition-all duration-200',
              localAchievementType === 'team'
                ? 'border-blue-600 bg-blue-600'
                : 'border-gray-300 bg-white group-hover:border-blue-400'
            ]">
              <div v-if="localAchievementType === 'team'" class="w-full h-full rounded-full bg-white scale-[0.4] transition-transform duration-200"></div>
            </div>
          </div>
          <span :class="[
            'transition-colors duration-200',
            localAchievementType === 'team' 
              ? 'text-gray-900' 
              : 'text-gray-600 group-hover:text-gray-800'
          ]">
            团队成果
          </span>
        </label>
      </div>
    </div>

    <!-- 团队成员信息（始终显示） -->
    <div :class="['space-y-6', localAchievementType === 'individual' ? 'opacity-40' : 'opacity-100']">
      <div>
        <label class="block mb-2">
          团队成员信息 <span class="text-red-500">*</span>
        </label>
        <p class="text-sm text-gray-500 mb-3">
          请按顺序填写所有团队成员信息（包括您自己）。仅限本单位第一作者填写，不要重复填报。
        </p>
      </div>

      <div class="flex items-center justify-end">
        <button
          type="button"
          @click="addTeamMember"
          :disabled="localAchievementType === 'individual'"
          class="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          添加成员
        </button>
      </div>

      <div class="bg-gray-50 rounded-lg p-4 space-y-3">
        <p v-if="localTeamMembers.length === 0" class="text-sm text-gray-500 text-center py-4">
          请按顺序添加团队成员信息
        </p>

        <div
          v-for="(member, index) in localTeamMembers"
          :key="member.id"
          :draggable="localAchievementType !== 'individual'"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
          @drop="handleDrop($event, index)"
          :class="[
            'bg-white rounded-lg border border-gray-200 transition-opacity',
            draggedIndex === index ? 'opacity-50' : 'opacity-100'
          ]"
        >
          <div class="flex items-center gap-3 p-3">
            <!-- 拖拽手柄 -->
            <div :class="['flex-shrink-0', localAchievementType === 'individual' ? 'cursor-not-allowed text-gray-300' : 'cursor-move text-gray-400 hover:text-gray-600']">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="9" cy="5" r="1.5"/>
                <circle cx="15" cy="5" r="1.5"/>
                <circle cx="9" cy="12" r="1.5"/>
                <circle cx="15" cy="12" r="1.5"/>
                <circle cx="9" cy="19" r="1.5"/>
                <circle cx="15" cy="19" r="1.5"/>
              </svg>
            </div>

            <!-- 位次 -->
            <div class="flex-shrink-0 w-16">
              <div class="text-sm text-gray-500 mb-1">位次</div>
              <div class="h-9 flex items-center justify-center bg-gray-50 rounded border border-gray-200">
                <span class="text-gray-700">{{ index + 1 }}</span>
              </div>
            </div>

            <!-- 姓名 -->
            <div class="flex-1 min-w-0">
              <label :for="`name-${member.id}`" class="block mb-1 text-sm text-gray-600">
                姓名 <span class="text-red-500">*</span>
              </label>
              <input
                :id="`name-${member.id}`"
                v-model="member.name"
                @input="updateTeamMember(member.id, 'name', $event.target.value)"
                :disabled="localAchievementType === 'individual'"
                type="text"
                placeholder="请输入姓名"
                class="w-full h-9 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- 身份 -->
            <div class="flex-1 min-w-0">
              <label class="block mb-1 text-sm text-gray-600">
                身份 <span class="text-red-500">*</span>
              </label>
              <el-select
                v-model="member.identity"
                :disabled="localAchievementType === 'individual'"
                placeholder="请选择身份"
                class="w-full"
                @change="(val) => updateTeamMember(member.id, 'identity', val)"
              >
                <el-option value="teacher" label="教师" />
                <el-option value="student" label="学生" />
                <el-option value="social" label="社会人士" />
              </el-select>
            </div>

            <!-- 所属单位 -->
            <div class="flex-1 min-w-0">
              <label :for="`unit-${member.id}`" class="block mb-1 text-sm text-gray-600">
                所属单位 <span class="text-red-500">*</span>
              </label>
              <input
                :id="`unit-${member.id}`"
                v-model="member.unit"
                @input="updateTeamMember(member.id, 'unit', $event.target.value)"
                :disabled="localAchievementType === 'individual'"
                type="text"
                placeholder="请输入所属单位"
                class="w-full h-9 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- 删除按钮 -->
            <div class="flex-shrink-0 pt-6">
              <button
                type="button"
                @click="removeTeamMember(member.id)"
                :disabled="localAchievementType === 'individual'"
                class="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center justify-center disabled:opacity-50"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-start gap-2 text-sm text-gray-500">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="8" cy="6" r="1.5"/>
            <circle cx="16" cy="6" r="1.5"/>
            <circle cx="8" cy="12" r="1.5"/>
            <circle cx="16" cy="12" r="1.5"/>
            <circle cx="8" cy="18" r="1.5"/>
            <circle cx="16" cy="18" r="1.5"/>
          </svg>
          <p>
            拖动左侧图标可调整成员顺序，位次将自动更新。
          </p>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

// Props
const props = defineProps({
  achievementType: {
    type: String,
    default: ''
  },
  teamMembers: {
    type: Array,
    default: () => []
  },
  firstAuthorLabel: { type: String, default: '' },
  firstInUnitLabel: { type: String, default: '' }
})

// Emits
const emit = defineEmits(['update:achievementType', 'update:teamMembers'])

// 本地状态
const localAchievementType = ref(props.achievementType)
const localTeamMembers = ref([...props.teamMembers])

// 拖拽状态
const draggedIndex = ref(null)

// 监听props变化
watch(() => props.achievementType, (newVal) => {
  localAchievementType.value = newVal
})

watch(() => props.teamMembers, (newVal) => {
  localTeamMembers.value = [...newVal]
}, { deep: true })

// 方法
const handleAchievementTypeChange = () => {
  emit('update:achievementType', localAchievementType.value)
  // 切换为单人成果时清空团队成员
  if (localAchievementType.value === 'individual') {
    localTeamMembers.value = []
    emit('update:teamMembers', [])
  }
}

const addTeamMember = () => {
  const newMember = {
    id: Date.now().toString(),
    name: '',
    identity: '',
    unit: '',
    rank: localTeamMembers.value.length + 1
  }
  localTeamMembers.value.push(newMember)
  emit('update:teamMembers', localTeamMembers.value)
}

const removeTeamMember = (id) => {
  const updatedMembers = localTeamMembers.value.filter(m => m.id !== id)
  // 重新排序位次
  const reorderedMembers = updatedMembers.map((m, index) => ({
    ...m,
    rank: index + 1
  }))
  localTeamMembers.value = reorderedMembers
  emit('update:teamMembers', reorderedMembers)
}

const updateTeamMember = (id, field, value) => {
  const updatedMembers = localTeamMembers.value.map(m =>
    m.id === id ? { ...m, [field]: value } : m
  )
  localTeamMembers.value = updatedMembers
  emit('update:teamMembers', updatedMembers)
}

// 拖拽事件处理
const handleDragStart = (e, index) => {
  draggedIndex.value = index
  e.dataTransfer.effectAllowed = 'move'
  e.target.style.opacity = '0.5'
}

const handleDragEnd = (e) => {
  e.target.style.opacity = '1'
  draggedIndex.value = null
}

const handleDragOver = (e) => {
  e.preventDefault()
  e.dataTransfer.dropEffect = 'move'
}

const handleDragEnter = (e) => {
  e.preventDefault()
}

const handleDrop = (e, dropIndex) => {
  e.preventDefault()
  
  if (draggedIndex.value === null || draggedIndex.value === dropIndex) {
    return
  }
  
  const dragMember = localTeamMembers.value[draggedIndex.value]
  const newMembers = [...localTeamMembers.value]
  newMembers.splice(draggedIndex.value, 1)
  newMembers.splice(dropIndex, 0, dragMember)
  
  // 重新分配位次
  const reorderedMembers = newMembers.map((m, index) => ({
    ...m,
    rank: index + 1
  }))
  
  localTeamMembers.value = reorderedMembers
  emit('update:teamMembers', reorderedMembers)
  
  draggedIndex.value = null
}
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.border-l-2 {
  border-left-width: 2px;
}

.min-w-0 {
  min-width: 0;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.cursor-move {
  cursor: move;
}

.transition-opacity {
  transition-property: opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.scale-\[0\.4\] {
  transform: scale(0.4);
}
</style>